import { MouseEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { MenuAction } from '@/components/Menu/Actions';
import { I18N_KEYS } from '@/language/keys';
import { useNodeFiltersStore, type SourceType } from '@/state/nodes/nodes.state';
import { FiltersIcon } from '@/UI/Icons/FiltersIcon';
import ParentMenuItem from '@/components/Recipe/Menu/ParentMenuItem';
import { AppMenuItem } from '@/UI/AppContextMenu/AppMenu.styles';
import { CheckMarkIcon } from '@/UI/Icons/CheckMarkIcon';

const SOURCE_FILTER_OPTIONS: Array<{
  value: SourceType;
  translationKey: keyof typeof I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.FILTERS.MODE_TOGGLE;
}> = [
  { value: 'tools', translationKey: 'TOOLS' },
  { value: 'models', translationKey: 'MODELS' },
];

export const FiltersMenu = () => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const filterBtnRef = useRef<HTMLButtonElement>(null);

  const { t } = useTranslation();
  const { filters, setOrderBy, setSource } = useNodeFiltersStore();

  const items: MenuAction[] = useMemo(() => {
    const toggleSource = (value: SourceType) => {
      const current = filters.source;
      setSource(current.includes(value) ? current.filter((v) => v !== value) : [...current, value]);
    };

    return [
      {
        type: 'section_label',
        text: t(I18N_KEYS.GENERAL.FILTER),
      },
      {
        type: 'slot',
        el: (
          <ParentMenuItem id="type" label={t(I18N_KEYS.GENERAL.TYPE)} contextMenuWidth="160px">
            {SOURCE_FILTER_OPTIONS.map((option) => {
              const isSelected = filters.source.includes(option.value);
              return (
                <AppMenuItem key={option.value} onClick={() => toggleSource(option.value)} selected={isSelected}>
                  {t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.FILTERS.MODE_TOGGLE[option.translationKey])}
                  {isSelected && <CheckMarkIcon />}
                </AppMenuItem>
              );
            })}
          </ParentMenuItem>
        ),
      },
      // TODO(backend): add "Created by" submenu here (Weave / Workspace / Me, multiselect)
      // once isCreatedByMe is available on saved items from the menu endpoint.
      // Same pattern as Type above — toggle values in an array, show tags in LeftPanelV2.
      // See 03-design.md "Backend changes needed" for details.
      {
        type: 'divider',
      },
      {
        type: 'section_label',
        text: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.FILTERS.ORDERBY.LABEL),
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.FILTERS.ORDERBY.FEATURED),
        action: () => setOrderBy('featured'),
        selected: filters?.orderBy === 'featured',
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.FILTERS.ORDERBY.PRICE_ASC),
        action: () => setOrderBy('price_asc'),
        selected: filters?.orderBy === 'price_asc',
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.FILTERS.ORDERBY.PRICE_DESC),
        action: () => setOrderBy('price_desc'),
        selected: filters?.orderBy === 'price_desc',
      },
    ];
  }, [filters?.orderBy, filters?.source, setOrderBy, setSource, t]);

  return (
    <>
      <AppIconButton onClick={() => setMenuAnchorEl(filterBtnRef.current)} ref={filterBtnRef} width={24} height={24}>
        <FiltersIcon width={16} height={16} />
      </AppIconButton>
      <AppContextMenu
        width="160px"
        noClose
        open={!!menuAnchorEl}
        onClose={(e: MouseEvent) => {
          if ('stopPropagation' in e) {
            e.stopPropagation();
          }
          setMenuAnchorEl(null);
        }}
        anchorEl={menuAnchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        items={items}
      />
    </>
  );
};
